'use client';

/**
 * Fires a Impression Ready event for Google Analytics. *Note: Do not ever push PII into the data layer!!*
 *
 * @example
 * const { fireEvent } = useImpressionReadyEvent();
 * fireEvent(items);
 */

import { useCallback } from 'react';
import { sendGTMEvent } from '@next/third-parties/google';
import { useTranslations } from 'next-intl';
import { getValueOrElseEmptyString } from 'utils/string';
import {
  OrderDetailsItem,
  Availability,
  ItemPrice,
  SourcingDetail
} from 'services/order/types';
import { getInventoryStatus } from 'utils/analytics';
import {
  CHECKFITMENT,
  DEFAULT_BRAND,
  DEFAULT_BUSINESS_UNIT,
  EVENT_IMPRESSION_READY
} from 'constants/analyticsConstants';
import { LineItem } from 'types/parts-diagram';

type ImpressionReadyType = {
  products: OrderDetailsItem[] | LineItem[] | { [key: string]: unknown }[];
  cartId?: string;
  currencyCode?: string;
  dealerId?: string | number;
  model?: string;
  paymentMethods?: string[];
  selectedEquipment?: Pick<Equipment, 'model'>;
  storeName: string;
  list: string;
};

export const useImpressionReadyEvent = () => {
  const t = useTranslations();
  const CONTACT_YOUR_DEALER = t('CONTACT_YOUR_DEALER');

  const buildEvent = useCallback(
    ({
      cartId,
      currencyCode,
      dealerId,
      model,
      paymentMethods,
      products,
      selectedEquipment,
      storeName,
      list
    }: ImpressionReadyType) => {
      const cartDetails = {
        businessUnit: DEFAULT_BUSINESS_UNIT,
        cartId: getValueOrElseEmptyString(cartId),
        dealerId: getValueOrElseEmptyString(dealerId),
        paymentMethod: (paymentMethods ?? []).join(', ')
      };

      const impressions = products?.map((product, index) => {
        const availability = product.availability as Availability[];
        const sourcingDetails = product.sourcingDetails as SourcingDetail[];
        const priceWithoutGST = product.priceWithoutGST as ItemPrice;

        return {
          brand: DEFAULT_BRAND,
          businessUnit: DEFAULT_BUSINESS_UNIT,
          category: getValueOrElseEmptyString(product.category),
          checkFitment: CHECKFITMENT,
          classification: getValueOrElseEmptyString(product.classification),
          id: getValueOrElseEmptyString(product.partNumber),
          inventoryLocation: getValueOrElseEmptyString(storeName),
          inventoryStatus: getValueOrElseEmptyString(
            getInventoryStatus(
              CONTACT_YOUR_DEALER,
              availability,
              sourcingDetails
            )
          ),
          kitId: getValueOrElseEmptyString(product.kitId),
          list: getValueOrElseEmptyString(list),
          msrp: getValueOrElseEmptyString(product.msrp),
          name: getValueOrElseEmptyString(product.name ?? product.description),
          packageFlag: getValueOrElseEmptyString(product.packageFlag),
          position: index + 1,
          price: getValueOrElseEmptyString(
            priceWithoutGST?.formatted?.totalPrice
          ),
          productAttributesVisible: getValueOrElseEmptyString(
            product.productAttributesVisible
          ),
          productWeight: getValueOrElseEmptyString(product.weight),
          quantity: getValueOrElseEmptyString(product.quantity),
          relatedModel: getValueOrElseEmptyString(
            selectedEquipment?.model || model
          )
        };
      });

      return {
        cartDetails,
        ecommerce: {
          currencyCode: getValueOrElseEmptyString(currencyCode),
          impressions
        },
        event: EVENT_IMPRESSION_READY
      };
    },
    [CONTACT_YOUR_DEALER]
  );

  const fireEvent = useCallback(
    (data: ImpressionReadyType) => sendGTMEvent(buildEvent(data)),
    [buildEvent]
  );

  return { fireEvent };
};
